import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { t } from 'i18next';

import { User } from 'types/interfaces/user/User';

import { Message } from 'api/DialogsApi';
import { getNotificationMessagePreview } from 'helpers/chat';
import { getDeclineTypeTestCookie } from 'helpers/payment/helpers';
import { getPhotoPlaceholders } from 'helpers/photoPlaceholders';
import { isMonetizationPage } from 'helpers/route';
import { assertString } from 'helpers/utils/checkType';
import { setIsBanned } from 'store/auth/authSlice';
import { setHasNotifications } from 'store/common/commonSlice';
import { readMessage } from 'store/messenger/messengerSlice';
import {
  addNewMatchDialogFromSocket,
  addNewMessageFromSocket,
  readMessages,
} from 'store/messenger/thunks';
import {
  setOneClickPaymentCardToken,
  setOneClickPaymentDeclineType,
  setOneClickPaymentOrderId,
  setOneClickPaymentStatus,
  setOneClickPaymentVerifyUrl,
} from 'store/oneClickPayment/oneClickPaymentSlice';
import { setCreditsAmount } from 'store/payment/paymentSlice';

import { useNotificationsQueryCache } from './queries/useNotificationsQuery';
import { useMatchesNotifications } from './useMatchesNotifications';
import { useToast } from './useToast';

enum WsActionTypes {
  Send = 'send',
  Read = 'read',
}

export type WebSocketMessage = {
  data: {
    action: WsActionTypes;
    contact: number;
    message?: Message;
  };
};

export const useWebSocketHandlers = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { pathname } = useLocation();

  const { showMessageToast } = useToast();

  const { matchNotificationHandler } = useMatchesNotifications();
  const { setNotificationsLikes, setNotificationsMatches } =
    useNotificationsQueryCache();

  const messageHandler = useCallback(
    (event: any) => {
      const action = event?.data?.action;
      if (!action) {
        return;
      }

      switch (action) {
        case WsActionTypes.Send:
          {
            if (!event?.data?.message || !event?.data?.contact) {
              return;
            }

            const { contact, message: newMessage } = event.data as {
              contact: User;
              message: Message;
            };

            dispatch(
              addNewMessageFromSocket({
                message: newMessage,
                contactId: contact.id,
              })
            );

            if (pathname === `/threads/${contact.id}`) {
              dispatch(readMessages({ contactId: contact.id }));
            }

            if (
              !pathname.match(/\/threads(\/\d+)?/) &&
              !isMonetizationPage(pathname)
            ) {
              showMessageToast({
                content: {
                  name: contact.username,
                  message: getNotificationMessagePreview(newMessage, t),
                  photo:
                    contact.photo_url ??
                    getPhotoPlaceholders(contact.gender, 1)[0],
                  onClick: () => {
                    history.push(`/threads/${contact.id}`);
                  },
                },
              });
            }
          }
          break;
        case WsActionTypes.Read:
          {
            const contactId = event?.data?.contact;
            const isIncoming = Boolean(event?.data?.incoming);

            if (!contactId) {
              return;
            }

            dispatch(readMessage({ contactId, isIncoming }));
          }
          break;
        default:
          break;
      }
    },
    [dispatch, history, pathname, showMessageToast]
  );

  const notificationsHandler = useCallback(
    (event: any) => {
      if (!event?.data) return;

      const { has, action, contact } = event.data;

      switch (action) {
        case 'has':
          dispatch(setHasNotifications(has));
          break;

        case 'video_upload':
          showMessageToast({
            content: {
              name: contact.username,
              message: `${contact.username} uploads new video!`,
              photo:
                contact.photo_url ?? getPhotoPlaceholders(contact.gender, 1)[0],
              onClick: () => {
                history.push(
                  `/user${contact.id}/videos?funnel=videonotification`
                );
              },
            },
          });
          break;

        case 'match': {
          matchNotificationHandler(contact);

          dispatch(
            addNewMatchDialogFromSocket({
              preview: 'It’s a Perfect Match',
              contact,
            })
          );

          setNotificationsMatches({
            count_new: event?.data?.count_new,
            title: '',
            description: '',
          });
          break;
        }

        default:
          break;
      }
    },
    [
      dispatch,
      history,
      matchNotificationHandler,
      setNotificationsMatches,
      showMessageToast,
    ]
  );

  const newLikesHandler = useCallback(
    (event: any) => {
      const { data } = event;

      setNotificationsLikes(data);
    },
    [setNotificationsLikes]
  );

  const paymentOrderHandler = useCallback(
    (event: any) => {
      const { data } = event;

      const testDeclineType = getDeclineTypeTestCookie();

      if (data?.order?.product_type === 1) {
        return;
      }

      if (assertString(data?.verify_url)) {
        dispatch(setOneClickPaymentVerifyUrl(data?.verify_url));
      }

      if (data.credits) {
        dispatch(setCreditsAmount(data.credits));
      }

      if (testDeclineType && data?.order?.status === 'error') {
        dispatch(setOneClickPaymentDeclineType(testDeclineType));
      } else {
        dispatch(
          setOneClickPaymentDeclineType(data?.order?.decline_type ?? null)
        );
      }

      dispatch(setOneClickPaymentCardToken(data?.order?.card?.id ?? null));
      dispatch(setOneClickPaymentOrderId(data?.order?.id ?? null));
      dispatch(setOneClickPaymentStatus(data?.order?.status ?? 'error'));
    },
    [dispatch]
  );

  const userInfoHandler = useCallback(
    (event: any) => {
      const action = event?.data?.action;

      if (action === 'user_banned') {
        dispatch(setIsBanned(true));
      }
    },
    [dispatch]
  );

  return {
    messageHandler,
    notificationsHandler,
    newLikesHandler,
    paymentOrderHandler,
    userInfoHandler,
  };
};
